/**
 * FICHE ANNONCE — refonte « bonnes affaires » du 2026-09-14.
 *
 * Même hiérarchie que la carte de la vitrine : photo, modèle, PRIX en gros,
 * cote barrée, écart en vert. Le prix ancre, l'écart qualifie.
 *
 * Ouverte aux visiteurs : tout est visible sauf le lien et la source. Le
 * bouton dit ce qu'il fait — « Créer un compte gratuit pour voir l'annonce »
 * pour un visiteur (retour sur cette fiche après l'inscription), « Voir
 * l'annonce » pour un compte connecté, avec depuis quand on ne l'a pas revue.
 * Plus d'abonnement depuis le 2026-10-22 : aucun mot de paiement ici. Le
 * masquage est fait par le serveur.
 *
 * Retirés de l'ancienne fiche : la note « /100 » de l'analyse IA et son bloc
 * « valeur vs marché » (deux chiffres forts qui répétaient l'écart), le bloc
 * « Outils revendeur — inclus » montré à des gens qui ne les ont pas.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import GrilleAffaires from '../components/Affaires/GrilleAffaires';
import VerdictPrix from '../components/Affaires/VerdictPrix';
import { nombre, nomSource, vueDepuis, vueIlYA } from '../components/Affaires/format';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { hasResellerPlan } from '../utils/profileAccess';
import { daysOnline, marketGap, shareListing } from '../utils/listing';
import '../components/Affaires/Affaires.css';
import './FicheAnnonce.css';

/* Même zone morte que le bloc verdict : à ±3 % de la cote, le prix est juste. */
const ZONE_MORTE_PCT = 3;

function nomVoiture(a) {
  // Libellés du serveur (« Citroën », « Série 3 ») ; la valeur brute en repli.
  return [a.marque_libelle || a.brand, a.modele_libelle || a.model].filter(Boolean).join(' ') || a.title || 'Voiture';
}


function Galerie({ images, pct }) {
  const [active, setActive] = useState(0);
  const [ko, setKo] = useState({});
  const liste = (images || []).filter((i) => typeof i === 'string' && i.startsWith('http')).slice(0, 8);
  const src = liste[active];
  const visible = src && !ko[active];

  return (
    <div className="zk-fiche-galerie">
      <div className={`zk-fiche-photo${visible ? '' : ' vide'}`}>
        {visible
          ? <img src={src} alt="" referrerPolicy="no-referrer" onError={() => setKo((k) => ({ ...k, [active]: true }))} />
          : <span>Photo indisponible</span>}
        {pct != null && pct >= ZONE_MORTE_PCT && <span className="zk-aff-pct">−{Math.round(pct)} %</span>}
      </div>
      {liste.length > 1 && (
        <div className="zk-fiche-miniatures">
          {liste.map((m, i) => (
            <button key={m} type="button" aria-label={`Photo ${i + 1}`} aria-pressed={i === active} onClick={() => setActive(i)}>
              <img src={m} alt="" loading="lazy" referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.parentElement.hidden = true; }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Historique({ releves, jours }) {
  if (releves.length >= 2) {
    return (
      <ol className="zk-fiche-releves">
        {releves.map((r, i) => {
          const precedent = releves[i - 1];
          const diff = precedent ? r.price - precedent.price : 0;
          return (
            <li key={`${r.recorded_at}-${i}`}>
              <span>{new Date(r.recorded_at).toLocaleDateString('fr-FR')}</span>
              <b>{nombre(r.price)} €</b>
              {diff !== 0 && (
                <span className={diff < 0 ? 'baisse' : 'hausse'}>
                  {diff < 0 ? '−' : '+'}{nombre(Math.abs(diff))} €
                </span>
              )}
            </li>
          );
        })}
      </ol>
    );
  }
  return (
    <p className="zk-fiche-note">
      Prix inchangé depuis que nous suivons cette annonce{jours != null ? ` (${jours} jour${jours > 1 ? 's' : ''})` : ''}.
    </p>
  );
}

function AnalyseIA({ analyse }) {
  return (
    <div className="zk-fiche-ia">
      {analyse.description && (
        <div className="zk-fiche-carte"><h3>Ce que dit vraiment l’annonce</h3><p>{analyse.description}</p></div>
      )}
      {analyse.avis && (
        <div className="zk-fiche-carte">
          <h3>Points forts et points d’attention</h3>
          <div className="zk-fiche-avis">
            <ul className="positifs">{(analyse.avis.positifs || []).map((p) => <li key={p}>{p}</li>)}</ul>
            <ul className="negatifs">{(analyse.avis.negatifs || []).map((n) => <li key={n}>{n}</li>)}</ul>
          </div>
        </div>
      )}
      {analyse.frais && (
        <div className="zk-fiche-carte">
          <h3>Frais à prévoir <span className="zk-fiche-frais">~{nombre(analyse.frais.total_estime || 0)} €</span></h3>
          <ul className="zk-fiche-lignes">
            {(analyse.frais.detail || []).map((f) => (
              <li key={f.poste}><span>{f.poste}{f.urgence ? ` · ${f.urgence}` : ''}</span><b>{nombre(f.montant || 0)} €</b></li>
            ))}
          </ul>
        </div>
      )}
      {analyse.negociation && (
        <div className="zk-fiche-carte">
          <h3>Marge de négociation estimée</h3>
          <p className="zk-fiche-nego">{nombre(analyse.negociation.marge_min || 0)} € – {nombre(analyse.negociation.marge_max || 0)} €</p>
          {analyse.negociation.argument && <p>{analyse.negociation.argument}</p>}
        </div>
      )}
      {analyse.conseil_achat && (
        <div className="zk-fiche-carte"><h3>Conseil d’achat</h3><p>{analyse.conseil_achat}</p></div>
      )}
    </div>
  );
}

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [annonce, setAnnonce] = useState(null);
  const [etat, setEtat] = useState('chargement');
  const [releves, setReleves] = useState([]);
  const [similaires, setSimilaires] = useState(null);
  const [analyse, setAnalyse] = useState(null);
  const [marche, setMarche] = useState(null);
  const [lienEnCours, setLienEnCours] = useState(false);
  const [message, setMessage] = useState('');
  const [partage, setPartage] = useState('');
  const [export_, setExport] = useState(false);

  const connecte = !!user;
  // Export PDF : porte la marge de revente, donc réservé côté serveur aux
  // anciens plans revendeur (en pratique l'admin). Le reste des outils est
  // ouvert à tout compte connecté.
  const revendeur = hasResellerPlan(user);

  useEffect(() => {
    setEtat('chargement');
    setAnnonce(null);
    setAnalyse(null);
    setMarche(null);
    setMessage('');
    api.get(`/api/listings/${id}`)
      .then((r) => { setAnnonce(r.data); setEtat('ok'); })
      .catch((err) => setEtat(err.response?.status === 410 ? 'expiree' : 'introuvable'));
    api.get(`/api/listings/${id}/price-history`)
      .then((r) => setReleves(Array.isArray(r.data) ? r.data : []))
      .catch(() => setReleves([]));
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const params = annonce ? { marque: annonce.brand, modele: annonce.model, par_page: 5 } : { par_page: 4 };
    if (etat === 'chargement') return;
    api.get('/api/listings/affaires', { params })
      .then((r) => {
        const autres = (r.data.annonces || []).filter((a) => String(a.id) !== String(id)).slice(0, 4);
        setSimilaires({ ...r.data, annonces: autres });
      })
      .catch(() => setSimilaires(null));
  }, [annonce, etat, id]);

  /* L'analyse IA déjà faite est montrée à tout compte connecté. Une NOUVELLE
     analyse (appel Groq) n'est lancée que pour les comptes qui la lançaient
     déjà : l'ouvrir à chaque vue de chaque compte mangerait le quota. */
  useEffect(() => {
    if (!annonce || !connecte) return;
    const existante = annonce.ai_text_analysis?.deep;
    if (existante) { setAnalyse(existante); return; }
    if (!revendeur) return;
    api.post(`/api/listings/${annonce.id}/analyze`).then((r) => setAnalyse(r.data)).catch(() => {});
  }, [annonce?.id, connecte]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!annonce || !connecte) return;
    api.get(`/api/listings/${annonce.id}/market-analysis`).then((r) => setMarche(r.data)).catch(() => setMarche(null));
  }, [annonce?.id, connecte]); // eslint-disable-line react-hooks/exhaustive-deps

  async function voirAnnonce() {
    if (!annonce?.url || lienEnCours) return;
    setLienEnCours(true);
    const fenetre = window.open('', '_blank');
    try {
      const res = await api.post(`/api/listings/${annonce.id}/check-url`);
      if (res.data.alive === false) {
        if (fenetre) fenetre.close();
        setEtat('expiree');
      } else if (fenetre) {
        fenetre.location.href = res.data.url || annonce.url;
      }
    } catch (err) {
      if (err.response?.status === 410) {
        if (fenetre) fenetre.close();
        setEtat('expiree');
      } else if (fenetre) {
        fenetre.location.href = annonce.url;
      }
    } finally {
      setLienEnCours(false);
    }
  }

  /* Visiteur : l'inscription, puis retour sur CETTE fiche (?suivant=). */
  function creerCompte() {
    navigate(`/register?suivant=${encodeURIComponent(`/listings/${annonce.id}`)}`);
  }

  async function exporterPdf() {
    if (export_) return;
    setExport(true);
    try {
      const res = await api.get(`/api/listings/${annonce.id}/report.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `annonce_${annonce.id}_zenkart.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* le bouton reste disponible */ } finally {
      setExport(false);
    }
  }

  async function partager() {
    const r = await shareListing(annonce);
    setPartage(r === 'copied' ? 'Résumé copié.' : r === 'shared' ? 'Annonce partagée.' : '');
    if (r !== 'failed') setTimeout(() => setPartage(''), 3000);
  }

  const autres = similaires && similaires.annonces.length > 0 && (
    <section className="zk-fiche-similaires">
      <h2>{annonce ? `Autres bonnes affaires : ${nomVoiture(annonce)}` : 'Les bonnes affaires du moment'}</h2>
      <GrilleAffaires donnees={similaires} chargement={false} erreur="" />
      <Link to={annonce ? `/explore?marque=${encodeURIComponent(annonce.brand || '')}` : '/explore'} className="zk-vit-tout">
        Voir toutes les bonnes affaires
      </Link>
    </section>
  );

  if (etat === 'chargement') {
    return (
      <>
        <Navbar />
        <main className="zk-fiche" aria-busy="true"><div className="zk-fiche-attente" /></main>
      </>
    );
  }

  if (etat !== 'ok') {
    return (
      <>
        <Navbar />
        <main className="zk-fiche">
          <section className="zk-fiche-absente">
            <h1>{etat === 'expiree' ? 'Cette annonce n’est plus en ligne' : 'Annonce introuvable'}</h1>
            <p>
              {etat === 'expiree'
                ? 'Elle a été vendue, retirée par le vendeur ou a expiré. Voici d’autres voitures vendues sous leur valeur.'
                : 'Le lien est peut-être incomplet. Les bonnes affaires du moment sont juste en dessous.'}
            </p>
          </section>
          {autres}
        </main>
      </>
    );
  }

  const gap = marketGap(annonce);
  const jours = daysOnline(annonce.first_seen_at);
  const lieu = [annonce.location && !/^france/i.test(annonce.location) ? annonce.location : null,
    annonce.departement_nom ? `${annonce.departement_nom} (${annonce.departement})` : null].filter(Boolean).join(' · ');

  return (
    <>
      <Navbar />
      <main className="zk-fiche">
        <button type="button" className="zk-fiche-retour" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/explore'))}>
          ← Bonnes affaires
        </button>

        <div className="zk-fiche-grille">
          <Galerie images={annonce.images} pct={gap?.pct} />

          <section className="zk-fiche-infos">
            <h1>{nomVoiture(annonce)}{annonce.year ? ` ${annonce.year}` : ''}</h1>
            {annonce.title && <p className="zk-fiche-version">{annonce.title}</p>}
            <p className="zk-prix-grand">{annonce.price ? `${nombre(annonce.price)} €` : 'Prix non renseigné'}</p>
            {/* Verdict à quatre niveaux, calculé par le serveur (niveau_prix) — le même
                composant que le vérificateur et les cartes. */}
            <VerdictPrix
              v={annonce.niveau_prix || (gap ? { niveau: 'SOLIDE', prix: annonce.price, cote: gap.cote, comparables: annonce.comparables_count } : null)}
              marque={annonce.marque_libelle || annonce.brand}
              modele={annonce.modele_libelle || annonce.model}
            />

            <p className="zk-fiche-vue">
              {vueDepuis(jours)}
              {annonce.price_drop_amount > 0 && (
                <span className="zk-fiche-baisse"> · prix baissé de {nombre(annonce.price_drop_amount)} €</span>
              )}
            </p>

            <div className="zk-fiche-action">
              {!connecte ? (
                <>
                  <button type="button" className="zk-fiche-cta" onClick={creerCompte}>
                    Créer un compte gratuit pour voir l’annonce
                  </button>
                  <p className="zk-fiche-aide">
                    Email et mot de passe suffisent. <Link to={`/login?suivant=${encodeURIComponent(`/listings/${annonce.id}`)}`}>Déjà un compte ?</Link>
                  </p>
                </>
              ) : annonce.url ? (
                <>
                  <button type="button" className="zk-fiche-cta" onClick={voirAnnonce} disabled={lienEnCours}>
                    {lienEnCours ? 'Vérification du lien…' : `Voir l’annonce${annonce.source ? ` sur ${nomSource(annonce.source)}` : ''}`}
                  </button>
                  <p className="zk-fiche-aide">{vueIlYA(annonce.observee_il_y_a_jours)}</p>
                </>
              ) : (
                <p className="zk-fiche-aide">Le lien de cette annonce n’est pas disponible.</p>
              )}
              {message && <p className="zk-fiche-message" role="alert">{message}</p>}
            </div>

            <dl className="zk-fiche-specs">
              {annonce.year && <><dt>Année</dt><dd>{annonce.year}</dd></>}
              <dt>Kilométrage</dt><dd>{annonce.mileage != null ? `${nombre(annonce.mileage)} km` : '—'}</dd>
              {annonce.fuel_type && <><dt>Énergie</dt><dd>{annonce.fuel_type}</dd></>}
              {annonce.transmission && <><dt>Boîte</dt><dd>{annonce.transmission}</dd></>}
              {lieu && <><dt>Localisation</dt><dd>{lieu}</dd></>}
              {annonce.seller_type && (
                <><dt>Vendeur</dt><dd>{annonce.seller_type === 'private' ? 'Particulier' : 'Professionnel'}</dd></>
              )}
            </dl>

            {connecte && (
              <div className="zk-fiche-outils">
                {marche?.available && (
                  <p>
                    <b>{marche.comparables_count}</b> voitures similaires en vente · médiane {nombre(marche.median_price)} €
                    {marche.position_percent != null && ` · ${Math.abs(marche.position_percent)} % ${marche.position_percent <= 0 ? 'sous' : 'au-dessus de'} la médiane`}
                  </p>
                )}
                <div>
                  {revendeur && <button type="button" onClick={exporterPdf} disabled={export_}>{export_ ? 'Export…' : 'Exporter en PDF'}</button>}
                  <button type="button" onClick={partager}>Partager</button>
                </div>
                {partage && <p className="zk-fiche-note" role="status">{partage}</p>}
              </div>
            )}
          </section>
        </div>

        <section className="zk-fiche-bloc">
          <h2>Évolution du prix</h2>
          <Historique releves={releves} jours={jours} />
        </section>

        {analyse && (
          <section className="zk-fiche-bloc">
            <h2>Analyse de l’annonce</h2>
            <AnalyseIA analyse={analyse} />
          </section>
        )}

        {autres}
      </main>
    </>
  );
}
